import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  Typography,
} from "@material-ui/core"
import FullscreenIcon from "@material-ui/icons/Fullscreen"
import { Field, Formik } from "formik"
import { TextField } from "formik-material-ui"
import { isEmpty } from "lodash"
import { useCallback, useContext, useMemo } from "react"
import * as Yup from "yup"
import LoaderButton from "~/components/LoaderButton/index.jsx"
import App from "~/contexts/App"

import Box from "../../../components/Common/Box"
import DialogTitle from "../components/DialogTitle"
import useStyles from "./styles"

const FullscreenDialog = () => {
  const classes = useStyles()

  const { dialogs, closeDialog } = useContext(App)
  const thisDialog = dialogs?.["fullscreen"] || {}
  let mainVideoSecondsLeft = thisDialog?.data?.lessonVideoDuration
  let videoProgress = thisDialog?.data?.videoProgress
  let startSeconds = thisDialog?.data?.startSeconds

  const { open = false, data = {} } = thisDialog
  const { lockdown, ...restData } = data

  const handleClose = () => {
    closeDialog("fullscreen")
  }

  const maxDisplaySeconds = Math.floor(mainVideoSecondsLeft - startSeconds)

  const schema = useMemo(() => {
    return Yup.object({
      fullscreen_seconds: Yup.number()
        .moreThan(0, "Display seconds should be greater than 0")
        .max(
          maxDisplaySeconds,
          "Display seconds cannot exceed the length of the video",
        ),
    })
  }, [maxDisplaySeconds])

  const handleSubmit = useCallback((values, actions) => {
    closeDialog("fullscreen", true, values)
    actions.setSubmitting(false)
  }, [])

  const initialValues = useMemo(() => {
    // new fullscreen has data but not id
    if (!isEmpty(restData) && !restData.id) {
      return {
        ...restData,
        fullscreen_seconds: restData?.fullscreen_seconds || 8,
      }
      // id only pre-existing fullscreen data
    } else if (!isEmpty(restData) && restData.id) {
      return restData
    } else {
      return {
        fullscreen_seconds: "",
      }
    }
  }, [restData])

  const handleRemove = () => {
    closeDialog("fullscreen", true, undefined)
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ submitForm, resetForm, values }) => (
        <Dialog
          open={open}
          onClose={handleClose}
          TransitionProps={{ onExited: resetForm }}
        >
          <DialogTitle
            title="Fullscreen"
            subtitle="Put video into fullscreen"
            icon={
              <FullscreenIcon
                style={{
                  position: "relative",
                  top: 3,
                  marginLeft: 10,
                  color: "#888888",
                }}
              />
            }
            disableTypography
            timestamp={data.startSeconds}
          />
          <DialogContent>
            <InputLabel htmlFor="display-time" className={classes.inputLabel}>
              Display time
            </InputLabel>
            <Box display="flex" alignItems="center" maxWidth="50%">
              <FormControl fullWidth style={{ display: "flex" }}>
                <Field
                  component={TextField}
                  name="fullscreen_seconds"
                  id="display-time"
                  type="number"
                  variant="filled"
                  max={maxDisplaySeconds}
                  disabled={lockdown}
                />
              </FormControl>
              <Typography
                style={{
                  flexShrink: 0,
                  color: "#444444",
                  opacity: 0.3,
                  marginLeft: 8,
                  marginBottom: 14,
                }}
              >
                {`In seconds (${Math.floor(
                  mainVideoSecondsLeft - startSeconds,
                )} seconds left)`}{" "}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions style={{ justifyContent: "space-between" }}>
            <Box>
              {!isEmpty(restData) && (
                <Button
                  className={classes.removeButton}
                  onClick={handleRemove}
                  type="button"
                  size="small"
                  disabled={lockdown}
                >
                  Remove
                </Button>
              )}
            </Box>
            <Box>
              <Button
                onClick={handleClose}
                type="button"
                style={{ marginRight: "2vw" }}
              >
                Close
              </Button>
              <LoaderButton
                variant="contained"
                color="secondary"
                onClick={submitForm}
                type="button"
                disabled={lockdown}
              >
                Done
              </LoaderButton>
            </Box>
          </DialogActions>
        </Dialog>
      )}
    </Formik>
  )
}

export default FullscreenDialog
